import { t } from '@/utils/i18n'
import { useState, useEffect, memo } from 'react'
import useCorpSettingStore from '@/stores/corp-setting'
import { useNavigate } from 'react-router-dom'
import useCorpInfoStore from '@/stores/corp-info'
import { cal } from '@/utils'
import { Toast, Input } from 'antd-mobile'
import styles from './index.module.less'
// import { useWhyDidYouUpdate } from 'ahooks'

// const { debounce } = utils

interface IProps {
  status?: string // loading 状态
  initialValue: string | number // 初始值
  quantity: number // 起订量
  stock: number // 库存
  unitName: string // 当前单位
  isDebounce?: boolean // 是否使用防抖
  index?: number // 当前 spec 索引
  maxInputLen?: number // 最大输入长度
  onChange(value: string, index: number): void
}

let _timer: string | number | null | undefined | NodeJS.Timeout = null
const Stepper = memo((props: IProps) => {
  // useWhyDidYouUpdate('props', props);
  const {
    status,
    initialValue,
    stock,
    unitName,
    isDebounce = true,
    index = 0,
    onChange,
    quantity,
    maxInputLen = 8,
  } = props
  const navigate = useNavigate()
  const { corpSetting } = useCorpSettingStore()
  const { corpInfo } = useCorpInfoStore()
  const { accountId } = corpInfo
  const { stockOn, qualityPrecision } = corpSetting
  const minNumStep = quantity || 1 // 最小购买数量

  const [inputValue, setInputValue] = useState(initialValue) // 输入框数值
  const [showMinus, setShowMinus] = useState(true) // 显示减号
  const [showPlus, setShowPlus] = useState(true) // 显示加号
  const [disabledStatus, setDisabledStatus] = useState(true) // 输入框是否可用
  let _value = Number(initialValue) // 用于提交的数值，纯数据字段

  useEffect(
    () => {
      if (status === 'success') {
        success()
      } else if (status === 'fail') {
        fail()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [status]
  )

  useEffect(
    () => {
      initBtn(Number(initialValue))
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialValue, stock, minNumStep]
  )

  useEffect(() => {
    setInputValue(initialValue)
  }, [initialValue])
  /*
   * 判断按钮和输入框状态
   */
  function initBtn(_val?: number) {
    const val = Number(_val ?? inputValue)
    let minus = true
    let plus = true
    let input = true

    // 1.如果是0，减号禁用
    if (val <= 0) minus = false
    
    // 2.如果有库存限制，当前数量等于库存时，加号不可用
    if (stockOn && val === stock) {
      plus = false
    }

    // 3.如果有库存限制，当前数量为0，起订量大于库存时，加号不可用
    if (stockOn && val === 0 && minNumStep > stock) {
      plus = false
    }

    // 4 如果有库存限制，当前数量加 1 大于库存时，加号不可用
    if (stockOn && val + 1 > stock) {
      plus = false
    }

    // 5.如果有库存限制，库存小于等于0（存在负库存情况），加号和输入框不可用
    if (stockOn && stock <= 0) {
      plus = false
      input = false
    }

    // 6.如果有库存限制，且库存小于起订量，加号不可用，输入框也不可用
    if (stockOn && stock < minNumStep) {
      plus = false
      input = false
    }

    setShowMinus(minus)
    setShowPlus(plus)
    setDisabledStatus(input)
  }

  /*
   * 减号点击判断
   * 0.按钮禁用时不操作
   * 1.对小数修正小数位
   * 2.如果有起订量，判断起订量
   */
  function handleMinus(event: MouseEvent) {
    event.stopPropagation()
    if (!showMinus) return

    let val = Number(inputValue) || 0

    if (status !== 'pending') {
      if (stockOn && stock < minNumStep) {
        Toast.show(t('库存小于起订量'))
        return
      }
    }

    // 判断当前数量和起订量的关系，当前数量大于起订量时，按 1 相减，等于起订量时按起订量相减
    if (val > minNumStep) {
      val = cal.minus(val, 1)
    } else if (val === minNumStep) {
      Toast.show(
        t('起订量为{{minNumStep}}{{unitName}}', {
          minNumStep: minNumStep,
          unitName: unitName,
        })
      )
      val = cal.minus(val, minNumStep)
    } else {
      Toast.show(
        t('起订量为{{minNumStep}}{{unitName}}', {
          minNumStep: minNumStep,
          unitName: unitName,
        })
      )
      val = 0
    }

    // 小数修正
    if (`${val}`.match(/\.+/g)) {
      val = Number(cal.getCarry(qualityPrecision, val))
    }

    setInputValue(String(val))
    initBtn(val)
    if (isDebounce) {
      updateDebounce('minus', String(val))
    } else {
      update('minus', String(val))
    }
  }

  /*
   * 加号点击判断
   * 0.按钮禁用时不操作
   * 1.如果按起订量加购则+起订量，不是则+1，然后修正小数
   * 2.如果大于等于库存，修正到库存
   * 3.如果有起订量，且目前小于起订量，直接跳到起订量
   */
  function handlePlus(event: MouseEvent) {
    event.stopPropagation()
    if (!showPlus) {
      Toast.show(t('库存不足'))
      return
    }

    let val = Number(inputValue) || 0
    if (status !== 'pending') {
      if (stockOn) {
        if (stock < minNumStep) {
          Toast.show(t('库存小于起订量'))
          return
        }
      }
    }

    // 当前数量为 0 的时候，直接加起订量，之后每次加都是 1
    if (val === 0) {
      val = cal.plus(val, minNumStep)
    } else {
      val = cal.plus(val, 1)
    }

    // 小数修正
    if (`${val}`.match(/\.+/g)) {
      val = Number(cal.getCarry(qualityPrecision, val))
    }

    // 如果大于等于库存，修正到库存
    if (stockOn && val > stock) {
      val = stock
    }

    setInputValue(String(val))
    initBtn(val)

    if (isDebounce) {
      updateDebounce('plus', String(val))
    } else {
      update('plus', String(val))
    }
  }

  // 点击输入框时提醒
  function bindInputTap(event: MouseEvent) {
    event.stopPropagation()
    if (status !== 'pending') {
      if (stockOn && stock < minNumStep) {
        Toast.show(t('库存小于起订量'))
      }
    }
  }

  function bindInputBlur() {
    inputFilter(inputValue)
  }

  // 获得焦点时，如果是 0 则清空输入框
  function bindInputFocus() {
    if (inputValue === '0') {
      setInputValue('')
    }
  }

  /*
   * 输入框判断（输入的时候去抖，失焦的时候直接触发）
   * 1.对小数修正小数位
   * 2.如果等于0，则不进行后续判断
   * 3.如果小于起订量，修正到起订量，抛出提示
   * 4.如果大于库存，修正到库存，抛出提示
   */
  async function inputFilter(v: any) {
    let val = v

    // 含有非数字和小数点的值时重置为0
    if (/[^\d.]/g.test(val) || !val) {
      val = 0
    } else {
      // 以小数点开头视为0
      if (String(val).startsWith('.')) {
        val = 0
      }

      if (String(val).indexOf('.') !== -1) {
        val = val.substr(0, val.indexOf('.'))
      }

      // 暂时不考虑小数点后面的值
      // 数量精度为0时，取小数点前面的值
      // if (qualityPrecision === 0 && val.indexOf('.') !== -1) {
      //   val = val.substr(0, val.indexOf('.'))
      // } else if (qualityPrecision !== 0) {
      //   if (val.match(/\.+/g) && val.match(/\./g).length > 1) {
      //     val = val.substr(0, val.lastIndexOf('.'))
      //   }

      //   if (val.match(/\.+/g)) {
      //     console.log(val)
      //     val = cal.getCarry(qualityPrecision, val)
      //   }
      // }
    }

    if (val > 0) {
      // 小于起订量时，修正为起订量
      if (val < minNumStep) {
        val = minNumStep

        Toast.show(
          t('最低起订量为{{minNumStep}}{{unitName}}', {
            minNumStep: minNumStep,
            unitName: unitName,
          })
        )
      }

      // 大于库存时，修正为库存数量
      if (val > stock && stockOn) {
        val = stock

        Toast.show(
          t('最大库存为{{stock}}{{unitName}}', { stock: stock, unitName: unitName })
        )
      }
    }

    setInputValue(String(val))

    initBtn()
    update('input', String(val))
  }

  /*
   * 更新数据事件
   * 如果是点击加减号需要去抖，直接输入则不需要去抖
   * 如果数值没有改变则不操作，如果数值发生改变则触发onchange事件
   */
  function updateDebounce(type: string, value: string) {
    if (_timer) clearTimeout(_timer)

    _timer = setTimeout(() => {
      _timer = null
      update(type, value)
    }, 300)
  }
  /**
   *
   * @param type 加减号
   * @param value 当前输入数量
   */
  function update(_: string, value: string) {
    if (value !== String(_value)) {
      onChange && onChange(value, index)
    }
  }

  /*
   * 更新数据回调
   * 通过status来触发
   */
  function success() {
    _value = Number(inputValue)
  }

  function fail() {
    setInputValue(String(_value))
    initBtn()
  }

  function handleGoLogin() {
    // Toast.show('请先登录')
    navigate('/login')
  }


  return (
    <div className={`${styles['flex-box']} ${styles['stepper']}`}>
      {!accountId ? (
        <div
          className={styles['disabled-mark']}
          onClick={() => handleGoLogin()}
        ></div>
      ) : (
        ''
      )}

      <div
        className={`${styles['stepper-img']} ${styles['minus-img']} ${
          !showMinus ? styles['disabled-img'] : ''
        }`}
        onClick={handleMinus}
      ></div>

      <Input
        className={styles['stepper-input']}
        type="tel"
        pattern="[0-9]*"
        disabled={!disabledStatus} // || status === "pending"
        value={String(inputValue)}
        maxLength={maxInputLen}
        onChange={setInputValue}
        onClick={bindInputTap}
        onBlur={bindInputBlur}
        onFocus={bindInputFocus}
      />

      <div
        className={`${styles['stepper-img']} ${
          showPlus ? styles['plus-img'] : styles['plus-disabled-img']
        }`}
        onClick={handlePlus}
      ></div>

      {/*  转圈加载  */}
      {
        status === "pending" &&
        <div className={ styles["stepper-loading"] }>
          <div className={ styles["loader-ring"] }></div>
        </div>
        }
    </div>
  )
})

export default Stepper

